// User registry — the staff directory the scope, queue and assignment surfaces
// read. Ported from legacy loadUsers/userName/shortName/activeUsers/
// assignableUsers (v20 lines ~1049-1057, 4514-4519).
//
// Lives in its OWN module (not assign.js) because BOTH the pure user helpers
// and the assignment WRITERS need it, and queue/scope must not import the
// write-side assign.js just to name an owner. The pure selectors are exported
// map-first (usersMap in, no store read) so they unit-test without a store;
// the store-reading convenience wrappers sit on top.

import { store, SLICES } from '../state/store.js';
import { getClient } from '../db/client.js';
import { ok, fail } from '../db/client.js';
import { TABLES } from '../db/contract.js';

// ─── Pure selectors (usersMap = { user_id: {full_name, role, is_active} }) ──

/**
 * Active staff only. is_active null/undefined counts as ACTIVE — older rows may
 * predate the column, and a legitimate nurse must never vanish from a picker
 * because a nullable flag was never backfilled. Exact legacy rule.
 * @param {Record<string, any>} usersMap
 * @returns {any[]}
 */
export function activeUsers(usersMap) {
  return Object.values(usersMap || {}).filter((u) => u && u.is_active !== false);
}

/**
 * Who may OWN patients: active nurses (and a nurse-coordinator carrying a
 * caseload). Admins/programme-managers are NEVER assignable — they run the
 * programme, they don't hold patients (legacy assignableUsers). Excludes
 * inactive accounts AND admins.
 * @param {Record<string, any>} usersMap
 * @returns {any[]}
 */
export function assignableUsers(usersMap) {
  return activeUsers(usersMap).filter((u) => !/admin|programmes/i.test(u.role || ''));
}

/**
 * "First L." compact display for owner tags on queue rows (legacy shortName).
 * A trailing parenthesised annotation — "(demo)", "(locum)" — is not a surname,
 * so it is dropped before shortening rather than yielding "Jane (.".
 * @param {any} n full name
 * @returns {string}
 */
export function shortName(n) {
  const p = String(n || '').trim().replace(/\s*\([^)]*\)\s*$/, '').split(/\s+/);
  return p.length > 1 ? p[0] + ' ' + p[p.length - 1][0] + '.' : p[0] || '—';
}

/**
 * Resolve a user_id to a display name against a directory + the signed-in
 * user, degrading exactly as legacy userName did: the directory wins; the
 * current user falls back to their own name (or 'You'); an id we can't resolve
 * is 'A team member'; a null id is 'Unknown'. Pure — tested directly.
 * @param {any} id user_id
 * @param {Record<string, any>} usersMap
 * @param {any} session SLICES.SESSION value ({user:{id}, fullName})
 * @returns {string}
 */
export function userNameFrom(id, usersMap, session) {
  if (!id) return 'Unknown';
  const u = usersMap && usersMap[id];
  if (u && u.full_name) return u.full_name;
  if (session && session.user && id === session.user.id) return session.fullName || 'You';
  return 'A team member';
}

/**
 * Reduce an app_users list to the id-keyed directory the store holds.
 * @param {any[]} list
 * @returns {Record<string, any>}
 */
export function usersById(list) {
  /** @type {Record<string, any>} */
  const map = {};
  for (const u of list || []) if (u && u.user_id) map[u.user_id] = u;
  return map;
}

// ─── Store-reading convenience wrappers ────────────────────────────────────

/** The directory currently in the store (empty until loadUsers runs). @returns {Record<string, any>} */
export function usersMap() {
  return store.get(SLICES.USERS) || {};
}

/**
 * Store-backed userName — the app-wide caller. Reads the loaded directory and
 * the session slice; pure logic lives in userNameFrom.
 * @param {any} id
 * @returns {string}
 */
export function userName(id) {
  return userNameFrom(id, usersMap(), store.get(SLICES.SESSION));
}

/**
 * Load the staff directory into SLICES.USERS (legacy loadUsers). Never throws;
 * a failed load leaves whatever directory was already present — assignment
 * pickers do a silent retry before showing their empty-state (Phase 3 UI).
 * @param {{client?: any}} [deps]
 * @returns {Promise<{ok: boolean}>}
 */
export async function loadUsers(deps) {
  const client = (deps && deps.client) || getClient();
  /** @type {any} */
  let res;
  try {
    res = await client.from(TABLES.APP_USERS).select('user_id,full_name,role,is_active');
  } catch (e) {
    res = { data: null, error: e };
  }
  if (res.error) {
    console.warn('loadUsers', res.error);
    return fail(res.error);
  }
  store.set(SLICES.USERS, usersById(res.data || []));
  return ok(res.data || []);
}
